import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Icon,
  Progress,
  Badge,
  Alert,
  Card,
  Heading,
} from '@chakra-ui/react';
import { toaster } from '../../components/ui/toaster';
import { FiUpload, FiFile, FiX, FiCheck, FiInfo, FiPlus } from 'react-icons/fi';
import { Policy } from '../../types';
import { useTranslation } from 'react-i18next';

const { Body: CardBody, Header: CardHeader } = Card;

const DEFAULT_MAX_FILES = 5; // fallback until the plan limit loads
const MAX_TOTAL_BYTES = 50 * 1024 * 1024; // 50 MB hard cap

const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
];

interface PolicyUploadStepProps {
  policy: Policy | null;
  onPolicySelect: (policy: Policy | null) => void;
}

const PolicyUploadStep: React.FC<PolicyUploadStepProps> = ({
  policy,
  onPolicySelect,
}) => {
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [maxFiles, setMaxFiles] = useState(DEFAULT_MAX_FILES);
  const [maxFileSizeMb, setMaxFileSizeMb] = useState(10);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  // Load the actual plan limits instead of hardcoded values.
  // Response shape: { subscription: { limits: { max_documents_per_review, max_file_size_mb } } }
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    fetch('/api/user/subscription', {
      credentials: 'include',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const limits = data?.subscription?.limits || {};
        const files = Number(limits.max_documents_per_review);
        const sizeMb = Number(limits.max_file_size_mb);
        if (files > 0) setMaxFiles(files);
        if (sizeMb > 0) setMaxFileSizeMb(sizeMb);
      })
      .catch(() => {});
  }, []);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return t('createReview.steps.zeroBytes');
    const k = 1024;
    const sizes = [
      t('createReview.steps.bytes'),
      t('createReview.steps.kilobytes'),
      t('createReview.steps.megabytes'),
      t('createReview.steps.gigabytes'),
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const totalSize = pendingFiles.reduce((sum, f) => sum + f.size, 0);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const incoming = Array.from(newFiles);
    const valid: File[] = [];

    for (const file of incoming) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toaster.create({
          title: t('createReview.steps.invalidFileType'),
          description: t('createReview.steps.unsupportedFileFormat', { name: file.name }),
          type: 'error',
          duration: 5000,
        });
        continue;
      }
      valid.push(file);
    }

    setPendingFiles(prev => {
      const combined = [...prev];
      for (const file of valid) {
        // Skip exact duplicates
        if (combined.some(f => f.name === file.name && f.size === file.size)) continue;
        combined.push(file);
      }
      if (combined.length > maxFiles) {
        toaster.create({
          title: t('createReview.steps.tooManyFiles'),
          description: t('createReview.steps.maxFilesAllowed', { count: maxFiles }),
          type: 'warning',
          duration: 5000,
        });
        return combined.slice(0, maxFiles);
      }
      return combined;
    });
  }, [maxFiles, t]);

  const removeFile = useCallback((index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpload = useCallback(async () => {
    if (pendingFiles.length === 0) return;

    if (totalSize > MAX_TOTAL_BYTES) {
      toaster.create({
        title: t('createReview.steps.totalSizeTooLarge'),
        description: t('createReview.steps.totalSizeExceedsLimit', { size: formatFileSize(totalSize) }),
        type: 'error',
        duration: 5000,
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const policyName =
      pendingFiles.length === 1
        ? pendingFiles[0].name.replace(/\.[^/.]+$/, '')
        : pendingFiles.map(f => f.name.replace(/\.[^/.]+$/, '')).join(', ');

    try {
      const formData = new FormData();
      pendingFiles.forEach(file => formData.append('files', file));
      formData.append('name', policyName);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/policies', true);
      xhr.withCredentials = true; // send HttpOnly auth cookie (matches services/api.ts)
      const token = localStorage.getItem('access_token');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        setIsUploading(false);
        setUploadProgress(0);
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const result = JSON.parse(xhr.responseText);
            const uploadedPolicy: Policy = {
              id: result.policy_id?.toString() || result.file_id?.toString() || Date.now().toString(),
              tag: result.tag || policyName,
              name: policyName,
              filename: result.files?.join(', ') || policyName,
              file_size: totalSize,
              file_type: 'multi-file',
              file_count: result.file_count ?? pendingFiles.length,
              files: result.files ?? pendingFiles.map(f => f.name),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              ...result,
            };
            onPolicySelect(uploadedPolicy);
            setPendingFiles([]);
            toaster.create({
              title: t('createReview.steps.policyUploadedSuccessfully'),
              description: t('createReview.steps.filesUploadedReadyForReview', { count: result.file_count ?? pendingFiles.length }),
              type: 'success',
              duration: 3000,
            });
          } catch {
            toaster.create({
              title: t('createReview.steps.uploadFailed'),
              description: t('createReview.steps.couldNotParseServerResponse'),
              type: 'error',
              duration: 5000,
            });
          }
        } else {
          let errorMsg = t('createReview.steps.failedToUploadPolicyTryAgain');
          try { errorMsg = JSON.parse(xhr.responseText)?.detail || errorMsg; } catch {}
          toaster.create({
            title: t('createReview.steps.uploadFailed'),
            description: errorMsg,
            type: 'error',
            duration: 5000,
          });
        }
      };

      xhr.onerror = () => {
        setIsUploading(false);
        setUploadProgress(0);
        toaster.create({
          title: t('createReview.steps.uploadFailed'),
          description: t('createReview.steps.networkErrorTryAgain'),
          type: 'error',
          duration: 5000,
        });
      };

      xhr.send(formData);
    } catch {
      setIsUploading(false);
      setUploadProgress(0);
      toaster.create({
        title: t('createReview.steps.uploadFailed'),
        description: t('createReview.steps.failedToUploadPolicyTryAgain'),
        type: 'error',
        duration: 5000,
      });
    }
  }, [pendingFiles, totalSize, onPolicySelect, t]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = ''; // reset so same file can be re-added after removal
    }
  }, [addFiles]);

  const handleRemovePolicy = () => {
    onPolicySelect(null);
    setPendingFiles([]);
  };

  const isTotalTooLarge = totalSize > MAX_TOTAL_BYTES;

  return (
    <VStack gap={6} align="stretch">
      <Box>
        <Heading size="md" mb={2}>
          {t('createReview.steps.policyUploadTitle')}
        </Heading>
        <Text color="gray.600">
          {t('createReview.steps.policyUploadSubtitle', { count: maxFiles })}
        </Text>
      </Box>

      {!policy ? (
        <>
          {/* Drop zone */}
          <Card.Root
            variant="outline"
            border={dragActive ? '2px dashed' : '1px solid'}
            borderColor={dragActive ? 'brand.400' : 'gray.200'}
            bg={dragActive ? 'brand.50' : 'white'}
            transition="all 0.2s"
            cursor="pointer"
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <CardBody>
              <VStack gap={4} py={pendingFiles.length > 0 ? 4 : 8}>
                <Icon
                  as={FiUpload}
                  w={pendingFiles.length > 0 ? 8 : 12}
                  h={pendingFiles.length > 0 ? 8 : 12}
                  color={dragActive ? 'brand.500' : 'gray.400'}
                />
                <VStack gap={1}>
                  <Text fontSize={pendingFiles.length > 0 ? 'md' : 'lg'} fontWeight="semibold">
                    {dragActive ? t('createReview.steps.dropYourFilesHere') : t('createReview.steps.clickUploadOrDragDrop')}
                  </Text>
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    {t('createReview.steps.policySupportedFilesHelp', { count: maxFiles })}
                  </Text>
                </VStack>
              </VStack>
            </CardBody>
          </Card.Root>

          {/* Pending file list */}
          {pendingFiles.length > 0 && (
            <VStack gap={3} align="stretch">
              <HStack justify="space-between">
                <Text fontWeight="semibold" fontSize="sm">
                  {t('createReview.steps.selectedFilesCount', { selected: pendingFiles.length, max: maxFiles })}
                </Text>
                <Text fontSize="sm" color={isTotalTooLarge ? 'red.500' : 'gray.500'}>
                  {t('createReview.steps.totalSize', { size: formatFileSize(totalSize) })}
                  {isTotalTooLarge && t('createReview.steps.exceeds50MbLimit')}
                </Text>
              </HStack>

              {pendingFiles.map((file, idx) => (
                <Card.Root key={`${file.name}-${idx}`} variant="outline" size="sm">
                  <CardBody py={2} px={4}>
                    <HStack justify="space-between">
                      <HStack gap={3}>
                        <Icon as={FiFile} color="brand.500" />
                        <VStack align="start" gap={0}>
                          <Text fontSize="sm" fontWeight="medium">{file.name}</Text>
                          <Text fontSize="xs" color="gray.500">{formatFileSize(file.size)}</Text>
                        </VStack>
                      </HStack>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorPalette="red"
                        onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                        aria-label={t('createReview.steps.removeFileAria', { name: file.name })}
                      >
                        <Icon as={FiX} />
                      </Button>
                    </HStack>
                  </CardBody>
                </Card.Root>
              ))}

              {pendingFiles.length < maxFiles && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                >
                  <Icon as={FiPlus} mr={2} />
                  {t('createReview.steps.addMoreFiles')}
                </Button>
              )}

              {isUploading ? (
                <VStack gap={2} w="full">
                  <Text fontSize="sm" color="gray.600">
                    {t('createReview.steps.uploadingFiles', { count: pendingFiles.length })}
                  </Text>
                  <Progress.Root
                    value={uploadProgress}
                    size="sm"
                    w="full"
                    colorPalette="brand"
                    borderRadius="full"
                  />
                </VStack>
              ) : (
                <Button
                  colorPalette="brand"
                  onClick={handleUpload}
                  disabled={isTotalTooLarge}
                  w="full"
                >
                  <Icon as={FiUpload} mr={2} />
                  {t('createReview.steps.uploadFilesButton', { count: pendingFiles.length })}
                </Button>
              )}
            </VStack>
          )}
        </>
      ) : (
        <Card.Root variant="elevated">
          <CardHeader>
            <HStack justify="space-between">
              <HStack gap={3}>
                <Icon as={FiFile} w={5} h={5} color="brand.500" />
                <VStack align="start" gap={1}>
                  <Text fontWeight="semibold">{policy.tag || policy.name}</Text>
                  <Text fontSize="sm" color="gray.500">
                    {policy.file_count && policy.file_count > 1
                      ? `${policy.file_count} files • ${formatFileSize(policy.file_size || 0)}`
                      : formatFileSize(policy.file_size || 0)}
                  </Text>
                  {policy.files && policy.files.length > 1 && (
                    <VStack align="start" gap={0} mt={1}>
                      {policy.files.map((fname, i) => (
                        <Text key={i} fontSize="xs" color="gray.400">
                          • {fname}
                        </Text>
                      ))}
                    </VStack>
                  )}
                </VStack>
              </HStack>
              <Badge colorPalette="green" variant="subtle">
                <HStack gap={1}>
                  <Icon as={FiCheck} w={3} h={3} />
                  <Text>{t('createReview.steps.uploaded')}</Text>
                </HStack>
              </Badge>
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            <Button
              variant="outline"
              colorPalette="red"
              size="sm"
              onClick={handleRemovePolicy}
            >
              <Icon as={FiX} mr={2} />
              {t('createReview.steps.removeAndReupload')}
            </Button>
          </CardBody>
        </Card.Root>
      )}

      {/* Hidden file input — multiple enabled */}
      <Input
        ref={fileInputRef}
        id="file-input"
        type="file"
        accept=".pdf,.doc,.docx,.txt,.md"
        onChange={handleInputChange}
        display="none"
        multiple
      />

      {/* Upload Guidelines */}
      <Alert.Root status="info" borderRadius="md">
        <Alert.Indicator>
          <Icon as={FiInfo} />
        </Alert.Indicator>
        <Box>
          <Alert.Title>{t('createReview.steps.uploadGuidelines')}</Alert.Title>
          <Alert.Description>
            {t('createReview.steps.uploadGuidelineMaxFiles', { count: maxFiles })}<br />
            {t('createReview.steps.uploadGuidelineMaxSizeDyn', { fileSize: maxFileSizeMb, totalSize: 50 })}<br />
            {t('createReview.steps.uploadGuidelineFormats')}<br />
            {t('createReview.steps.uploadGuidelineCitations')}<br />
            {t('createReview.steps.uploadGuidelineStructuredDocuments')}
          </Alert.Description>
        </Box>
      </Alert.Root>
    </VStack>
  );
};

export default PolicyUploadStep;
